import { useState } from "react"
import { EmojiPicker } from "frimousse"
import { useFieldContext } from "@/lib/form-context"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { FormField, FormLabel, FormDescription, FieldInfo } from "@/components/ui/form"
import { cn } from "@/lib/utils"

interface EmojiPickerFieldProps {
  label: string
  required?: boolean
  description?: string
  placeholder?: string
}

export function EmojiPickerField({
  label,
  required,
  description,
  placeholder = "Pick an emoji",
}: EmojiPickerFieldProps) {
  const field = useFieldContext<string>()
  const [open, setOpen] = useState(false)

  return (
    <FormField>
      <FormLabel required={required}>{label}</FormLabel>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start font-normal",
              !field.state.value && "text-muted-foreground"
            )}
          >
            {field.state.value ? (
              <span className="text-lg">{field.state.value}</span>
            ) : (
              placeholder
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-fit p-0" align="start">
          <EmojiPicker.Root
            className="isolate flex h-[320px] w-[320px] flex-col bg-popover"
            onEmojiSelect={(emoji) => {
              field.handleChange(emoji.emoji)
              setOpen(false)
            }}
          >
            <EmojiPicker.Search
              autoFocus
              className="mx-2 mt-2 h-8 rounded-none border border-input bg-transparent px-2.5 text-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring/50"
              placeholder="Search emoji..."
            />
            <EmojiPicker.Viewport className="relative flex-1 outline-none">
              <EmojiPicker.Loading className="flex h-full items-center justify-center text-xs text-muted-foreground">
                Loading...
              </EmojiPicker.Loading>
              <EmojiPicker.Empty className="flex h-full items-center justify-center text-xs text-muted-foreground">
                No emoji found.
              </EmojiPicker.Empty>
              <EmojiPicker.List
                className="select-none pb-2"
                components={{
                  CategoryHeader: ({ category, ...props }) => (
                    <div
                      className="bg-popover px-2 pb-1.5 pt-2 text-xs font-medium text-muted-foreground"
                      {...props}
                    >
                      {category.label}
                    </div>
                  ),
                  Emoji: ({ emoji, ...props }) => (
                    <button
                      className="flex size-8 items-center justify-center rounded-sm text-lg hover:bg-muted"
                      {...props}
                    >
                      {emoji.emoji}
                    </button>
                  ),
                }}
              />
            </EmojiPicker.Viewport>
          </EmojiPicker.Root>
        </PopoverContent>
      </Popover>
      {description && <FormDescription>{description}</FormDescription>}
      <FieldInfo field={field} />
    </FormField>
  )
}
